import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { STATISTICS_FIELDS, getOperatorsForField, getFieldOptionConfig, getValueEditorType, useFieldOptionConfig } from '../../constants/segmentOptions';
import { useApp } from '../../contexts/AppContext';

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
  id: 'centerText',
  beforeDraw: (chart) => {
    const { width, height, ctx } = chart;
    ctx.restore();

    const fontSize = Math.min(width, height) / 20;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#333';

    const text = `${chart.config.options.plugins.centerText.filteredTotal}`;
    const subText = `of ${chart.config.options.plugins.centerText.totalVasts} total`;

    const textX = Math.round((width - ctx.measureText(text).width) / 2);
    const textY = height / 2 - fontSize / 2;

    ctx.fillText(text, textX, textY);

    ctx.font = `${fontSize * 0.6}px Arial`;
    ctx.fillStyle = '#666';
    const subTextX = Math.round((width - ctx.measureText(subText).width) / 2);
    const subTextY = textY + fontSize * 0.8;

    ctx.fillText(subText, subTextX, subTextY);
    ctx.save();
  }
};

export default function VastsDistributionChart() {
  const { performRequest } = useApp();
  const fieldOptionConfig = useFieldOptionConfig();

  const [mode, setMode] = useState('field');

  const [selectedField, setSelectedField] = useState('country');

  const [filters, setFilters] = useState([
    { id: 1, field: 'country', operator: 'eq', value: '' }
  ]);

  const [distribution, setDistribution] = useState([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [totalVasts, setTotalVasts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const nextFilterId = useRef(2);
  const requestIdRef = useRef(0);

  const addFilter = () => {
    setFilters(prev => [...prev, {
      id: nextFilterId.current++,
      field: 'country',
      operator: 'eq',
      value: ''
    }]);
  };

  const removeFilter = (filterId) => {
    if (filters.length > 1) {
      setFilters(prev => prev.filter(f => f.id !== filterId));
    } else {
      setFilters([{ id: 1, field: 'country', operator: 'eq', value: '' }]);
    }
  };

  const updateFilter = (filterId, updates) => {
    setFilters(prev => prev.map(filter =>
      filter.id === filterId
        ? { ...filter, ...updates }
        : filter
    ));
  };

  const fetchFieldDistribution = async () => {
    if (!selectedField) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        field: selectedField
      });

      const response = await performRequest(`/statistics/field-distribution?${params}`);

      if (currentRequestId === requestIdRef.current) {
        setDistribution(response.distribution || []);
        setTotalVasts(response.totalVasts || 0);
        setFilteredTotal(response.totalVasts || 0);
        setLoading(false);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error('Error fetching field distribution:', err);
        setError(err.message || 'Failed to fetch field distribution data');
        setDistribution([]);
        setFilteredTotal(0);
        setTotalVasts(0);
        setLoading(false);
      }
    }
  };

  const fetchFilterDistribution = async () => {
    if (filters.length === 0 || !filters[0].field) return;

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const apiFilters = filters
        .filter(f => f.field && f.value !== '' && f.value !== null && f.value !== undefined)
        .map(f => ({
          field: f.field,
          operator: f.operator || 'eq',
          value: f.value
        }));

      if (apiFilters.length === 0) {
        if (currentRequestId === requestIdRef.current) {
          setDistribution([]);
          setFilteredTotal(0);
          setTotalVasts(0);
          setLoading(false);
        }
        return;
      }

      const params = new URLSearchParams({
        filters: JSON.stringify(apiFilters)
      });

      const response = await performRequest(`/statistics/vasts-distribution?${params}`);

      if (currentRequestId === requestIdRef.current) {
        setDistribution(response.distribution || []);
        setFilteredTotal(response.filteredTotal || 0);
        setTotalVasts(response.totalVasts || 0);
        setLoading(false);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        console.error('Error fetching filter distribution:', err);
        setError(err.message || 'Failed to fetch filter distribution data');
        setDistribution([]);
        setFilteredTotal(0);
        setTotalVasts(0);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (mode === 'field') {
      fetchFieldDistribution();
    } else {
      fetchFilterDistribution();
    }
  }, [mode, selectedField, filters]);

  const renderValueEditor = (filter) => {
    const field = STATISTICS_FIELDS.find(f => f.value === filter.field);
    const operators = getOperatorsForField(filter.field);
    const selectedOperator = operators.find(op => op.value === filter.operator);
    const valueType = getValueEditorType(selectedOperator);
    const fieldConfig = getFieldOptionConfig(filter.field, fieldOptionConfig);

    if (valueType === 'none') {
      return null;
    }

    if (valueType === 'multi') {
      if (fieldConfig?.options) {
        return (
          <select
            multiple
            value={Array.isArray(filter.value) ? filter.value : []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, option => {
                const rawValue = fieldConfig.toRaw ? fieldConfig.toRaw(option.value) : option.value;
                return rawValue;
              });
              updateFilter(filter.id, { value: values });
            }}
            className="chart-filter-select"
            style={{ minHeight: '80px' }}
          >
            {fieldConfig.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}{option.kind ? ` [${option.kind}]` : ''}
              </option>
            ))}
          </select>
        );
      } else {
        return (
          <input
            type="text"
            value={Array.isArray(filter.value) ? filter.value.join(', ') : ''}
            onChange={(e) => {
              const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
              updateFilter(filter.id, { value: values });
            }}
            placeholder="Enter comma-separated values"
            className="chart-filter-input"
          />
        );
      }
    }

    if (valueType === 'range') {
      const rangeValue = Array.isArray(filter.value) ? filter.value : ['', ''];
      return (
        <div className="chart-filter-range-container">
          <input
            type="number"
            value={rangeValue[0] || ''}
            onChange={(e) => {
              const newRange = [e.target.value, rangeValue[1]];
              updateFilter(filter.id, { value: newRange });
            }}
            placeholder="Min"
            className="chart-filter-input"
          />
          <input
            type="number"
            value={rangeValue[1] || ''}
            onChange={(e) => {
              const newRange = [rangeValue[0], e.target.value];
              updateFilter(filter.id, { value: newRange });
            }}
            placeholder="Max"
            className="chart-filter-input"
          />
        </div>
      );
    }

    if (field?.type === 'boolean') {
      return (
        <select
          value={filter.value}
          onChange={(e) => updateFilter(filter.id, { value: e.target.value === 'true' })}
          className="chart-filter-select"
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (field?.type === 'datetime') {
      return (
        <input
          type="date"
          value={filter.value || ''}
          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
          className="chart-filter-select"
        />
      );
    }

    if (fieldConfig?.options) {
      return (
        <select
          value={fieldConfig.toString ? fieldConfig.toString(filter.value) : filter.value}
          onChange={(e) => {
            const rawValue = fieldConfig.toRaw ? fieldConfig.toRaw(e.target.value) : e.target.value;
            updateFilter(filter.id, { value: rawValue });
          }}
          className="chart-filter-select"
        >
          <option value="">Select...</option>
          {fieldConfig.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}{option.kind ? ` [${option.kind}]` : ''}
            </option>
          ))}
        </select>
      );
    }

    const inputType = field?.type === 'number' ? 'number' : 'text';
    return (
      <input
        type={inputType}
        value={filter.value || ''}
        onChange={(e) => {
          const value = field?.type === 'number' ? Number(e.target.value) : e.target.value;
          updateFilter(filter.id, { value });
        }}
        className="chart-filter-input"
        placeholder={`Enter ${field?.label || 'value'}`}
      />
    );
  };

  const chartData = {
    labels: distribution.map(item => item.label),
    datasets: [
      {
        data: distribution.map(item => item.value),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#E74C3C',
          '#2ECC71',
          '#F39C12',
          '#8E44AD',
          '#3498DB',
          '#E67E22'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 40,
        left: 20,
        right: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 25,
          usePointStyle: true,
          font: {
            size: 12
          },
          boxWidth: 15,
          boxHeight: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const percentage = totalVasts > 0 ? ((context.parsed / totalVasts) * 100).toFixed(1) : 0;
            const filteredPercentage = filteredTotal > 0 ? ((context.parsed / filteredTotal) * 100).toFixed(1) : 0;
            return [
              `${context.label}: ${context.parsed}`,
              `${percentage}% of total VASTs (${totalVasts})`,
              `${filteredPercentage}% of filtered results (${filteredTotal})`
            ];
          }
        }
      },
      centerText: {
        filteredTotal,
        totalVasts
      }
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: '#fff'
      }
    }
  };

  return (
    <div className="statistics-chart-container">
      <div className="statistics-chart-header">
        <h5 className="statistics-chart-title">Impressions Distribution Analysis</h5>
        <p className="statistics-chart-subtitle">Analyze Impressions distribution based on multiple filters (last month)</p>
      </div>
      <div className="statistics-chart-body">
        <div className="mode-switcher-section">
          <div className="mode-switcher">
            <button
              className={`mode-btn ${mode === 'field' ? 'active' : ''}`}
              onClick={() => setMode('field')}
            >
              <i className="fas fa-chart-pie"></i>
              <span>Field Distribution</span>
            </button>
            <button
              className={`mode-btn ${mode === 'filter' ? 'active' : ''}`}
              onClick={() => setMode('filter')}
            >
              <i className="fas fa-filter"></i>
              <span>Filter Analysis</span>
            </button>
          </div>
          <p className="mode-description">
            {mode === 'field'
              ? 'View distribution of all VASTs by a selected field'
              : 'Analyze VASTs using multiple filters and conditions'
            }
          </p>
        </div>

        {mode === 'field' && (
          <div className="field-distribution-section">
            <div className="field-selector-header">
              <h6 className="section-title">Select Field</h6>
            </div>
            <div className="field-selector-container">
              <div className="field-selector">
                <label className="field-selector-label">Distribution Field</label>
                <select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  className="chart-filter-select field-selector-input"
                >
                  {STATISTICS_FIELDS.map(field => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Filter Analysis Mode */}
        {mode === 'filter' && (
          <div className="filters-section">
            <div className="filters-header">
              <h6 className="filters-title">Filters</h6>
              <button
                type="button"
                onClick={addFilter}
                className="chart-filter-add-btn"
              >
                <i className="fas fa-plus"></i>
                <span>Add Filter</span>
              </button>
            </div>

            <div className="filters-container">
              {filters.map((filter, index) => (
                <div key={filter.id} className="filter-card">
                  <div className="filter-card-body">
                    <div className="filter-row">
                      <div className="filter-field">
                        <label className="filter-label">Field</label>
                        <select
                          value={filter.field}
                          onChange={(e) => {
                            const newField = e.target.value;
                            const operators = getOperatorsForField(newField);
                            updateFilter(filter.id, {
                              field: newField,
                              operator: operators[0]?.value || 'eq',
                              value: ''
                            });
                          }}
                          className="chart-filter-select"
                        >
                          {STATISTICS_FIELDS.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="filter-operator">
                        <label className="filter-label">Operator</label>
                        <select
                          value={filter.operator}
                          onChange={(e) => updateFilter(filter.id, { operator: e.target.value, value: '' })}
                          className="chart-filter-select"
                        >
                          {getOperatorsForField(filter.field).map(op => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="filter-value">
                        <label className="filter-label">Value</label>
                        {renderValueEditor(filter)}
                      </div>
                      <div className="filter-actions">
                        <div className="filter-actions-container">
                          <button
                            type="button"
                            onClick={() => removeFilter(filter.id)}
                            className="chart-filter-remove-btn"
                            title={filters.length > 1 ? "Remove filter" : "Reset filter"}
                          >
                            {filters.length > 1 ? (
                              <>
                                <i className="fas fa-trash-alt"></i>
                                <span className="btn-text">Delete</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-undo"></i>
                                <span className="btn-text">Reset</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    {index < filters.length - 1 && (
                      <div className="filter-connector">
                        <span className="filter-connector-badge">AND</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="chart-error-state">
            <i className="fas fa-exclamation-triangle chart-error-icon"></i>
            <p><strong>Error:</strong> {error}</p>
          </div>
        )}

        {loading && (
          <div className="chart-loading-state">
            <div className="chart-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <p>Loading chart data...</p>
          </div>
        )}

        {!loading && !error && distribution.length > 0 && (
          <div className="chart-section">
            {mode === 'field' && (
              <div className="chart-container">
                <div className="chart-canvas">
                  <Pie data={chartData} options={chartOptions} plugins={[centerTextPlugin]} />
                </div>
              </div>
            )}
            <div className={`chart-summary ${mode === 'filter' ? 'full-width' : ''}`}>
              <h6 className="summary-title">
                {mode === 'field' ? 'Field Distribution Summary' : 'Filter Analysis Results'}
              </h6>
              {mode === 'filter' && (
                <div className="filter-results-info">
                  <p className="filter-results-description">
                    Results showing VASTs matching your filter criteria
                  </p>
                  <div className="filter-results-summary">
                    <div className="filter-summary-item">
                      <span className="filter-summary-label">Matching VASTs:</span>
                      <span className="filter-summary-value">{filteredTotal}</span>
                    </div>
                    <div className="filter-summary-item">
                      <span className="filter-summary-label">Total VASTs:</span>
                      <span className="filter-summary-value">{totalVasts}</span>
                    </div>
                    <div className="filter-summary-item">
                      <span className="filter-summary-label">Match Rate:</span>
                      <span className="filter-summary-value">
                        {totalVasts > 0 ? ((filteredTotal / totalVasts) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="summary-stats">
                {mode === 'field' ? (
                  <>
                    <div className="summary-stat-card">
                      <div className="summary-stat-body">
                        <small className="summary-stat-label">Selected Field</small>
                        <div className="summary-stat-value-text">
                          {STATISTICS_FIELDS.find(f => f.value === selectedField)?.label || selectedField}
                        </div>
                      </div>
                    </div>
                    <div className="summary-stat-card">
                      <div className="summary-stat-body">
                        <small className="summary-stat-label">Total VASTs</small>
                        <div className="summary-stat-value">{totalVasts}</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="summary-stat-card">
                      <div className="summary-stat-body">
                        <small className="summary-stat-label">Filtered Results</small>
                        <div className="summary-stat-value">{filteredTotal}</div>
                      </div>
                    </div>
                    <div className="summary-stat-card">
                      <div className="summary-stat-body">
                        <small className="summary-stat-label">Total VASTs</small>
                        <div className="summary-stat-value">{totalVasts}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {mode === 'field' && (
                <div className="summary-table-container">
                  <table className="summary-table">
                    <thead>
                      <tr>
                        <th>Value</th>
                        <th>Count</th>
                        <th>% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distribution.map((item, index) => {
                        const totalPercentage = totalVasts > 0 ? ((item.value / totalVasts) * 100).toFixed(1) : 0;
                        return (
                          <tr key={index}>
                            <td className="summary-table-label" title={item.label}>
                              {item.label}
                            </td>
                            <td className="summary-table-value">{item.value}</td>
                            <td className="summary-table-percentage">{totalPercentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && distribution.length === 0 && (
          <div className="chart-empty-state">
            <i className="fas fa-chart-pie chart-empty-icon"></i>
            <p>No data found for the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}